import { createContext, useContext, useState, type ReactNode } from "react";
import { type CountryData, getCountryByCode, getGenericFallback } from "../data/countries";

/**
 * The country a visitor is moving from, picked in the plan form or set by a
 * country page (/:country). It personalises the FAQ and is carried into the
 * case at sign-up (lib/preselected-country.ts).
 */
interface CountryContextValue {
  selectedCode: string;
  country: CountryData;
  /** A country was chosen (plan form or country page), not just typed. */
  isConfirmed: boolean;
  resetSelections: () => void;
  /** Set the country and confirm it in one call (country pages, plan form). */
  setAndConfirm: (code: string, cityId?: string) => void;
}

const CountryContext = createContext<CountryContextValue>({
  selectedCode: "",
  country: getGenericFallback(""),
  isConfirmed: false,
  resetSelections: () => {},
  setAndConfirm: () => {},
});

export function CountryProvider({ children }: { children: ReactNode }) {
  const [selectedCode, setSelectedCode] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const country = (selectedCode && getCountryByCode(selectedCode)) || getGenericFallback("");

  return (
    <CountryContext.Provider
      value={{
        selectedCode,
        country,
        isConfirmed,
        resetSelections: () => {
          setSelectedCode("");
          setIsConfirmed(false);
        },
        setAndConfirm: (code: string) => {
          setSelectedCode(code);
          setIsConfirmed(true);
        },
      }}
    >
      {children}
    </CountryContext.Provider>
  );
}

export const useCountry = () => useContext(CountryContext);
